'use client';

import { toLabelValue } from '@/common/convertToDropdownOptions';
import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import { useDynamicLocale } from '@/hoslog-intl';
import { useQuery } from '@/react-query/hooks/useQuery/useQuery';
import { hoslogPlanRequestQueries } from '@/services/hoslog-plans/hoslog-plans-request.queries';
import { useMemo } from 'react';
import { z } from 'zod';
import useHoslogPlanRequestMutation from './planRequests/hooks/useHoslogPlanRequestMutation';

type PlanRequestDetail = {
  planTitle?: string;
  planTitleEng?: string;
  lastEntryTime?: string;
  planPrice?: number;
  nomination?: number;
  noOfPeople?: number;
};

const HoslogPlanRequestsForm = ({
  planRequestId,
  planId,
  clubId,
}: {
  planRequestId?: string;
  planId?: string;
  clubId?: string;
}) => {
  const formSchema = z.object({
    planTitle: z.string().min(1, { message: 'common.form.validation.required' }),
    planTitleEng: z.string().optional(),
    lastEntryTime: z.string().refine((value) => value !== '' && value !== 'default', {
      message: 'common.form.validation.required',
    }),
    planPrice: z.string().min(1, { message: 'common.form.validation.required' }),
    nomination: z.string().refine((value) => value !== '' && value !== 'default', {
      message: 'common.form.validation.required',
    }),
    noOfPeople: z.string().refine((value) => value !== '' && value !== 'default', {
      message: 'common.form.validation.required',
    }),
  });

  const {
    data,
    isLoading: isLoadingPlanData,
    isError,
  } = useQuery({
    ...hoslogPlanRequestQueries.details(planId || '', clubId || ''),
    enabled: !!(planId && clubId),
  });

  const { data: timeInterval } = useQuery({
    ...hoslogPlanRequestQueries.getTimeInterval(clubId || ''),
    enabled: !!clubId,
  });
  const timeIntervalOption = toLabelValue(timeInterval?.data ?? [], {
    labelKey: 'timeInterval',
    valueKey: 'timeInterval',
  });

  const numberOfPeopleAndNominationOption = [
    {
      label: '0名',
      value: '0',
      labelEng: '0名',
    },
    {
      label: '1名',
      value: '1',
      labelEng: '1名',
    },
    {
      label: '2名',
      value: '2',
      labelEng: '2名',
    },
    {
      label: '3名',
      value: '3',
      labelEng: '3名',
    },
    {
      label: '4名',
      value: '4',
      labelEng: '4名',
    },
    {
      label: '5名',
      value: '5',
      labelEng: '5名',
    },
    {
      label: '6名',
      value: '6',
      labelEng: '6名',
    },
    {
      label: '7名',
      value: '7',
      labelEng: '7名',
    },
    {
      label: '8名',
      value: '8',
      labelEng: '8名',
    },
    {
      label: '9名',
      value: '9',
      labelEng: '9名',
    },
    {
      label: '10名',
      value: '10',
      labelEng: '10名',
    },
  ];
  const initialValues = useMemo(() => {
    if (!data?.data || !planRequestId) {
      return {
        planTitle: '',
        planTitleEng: '',
        lastEntryTime: '',
        planPrice: '',
        nomination: '',
        noOfPeople: '',
      };
    }

    const planData = data.data as PlanRequestDetail;
    return {
      planTitle: planData.planTitle || '',
      planTitleEng: planData.planTitleEng || '',
      lastEntryTime: planData.lastEntryTime || '',
      planPrice: planData.planPrice?.toString() || '0',
      nomination: planData.nomination?.toString() || '',
      noOfPeople: planData.noOfPeople?.toString() || '',
    };
  }, [data?.data, planRequestId]);

  const { mutateAsync } = useHoslogPlanRequestMutation();
  const { t } = useDynamicLocale();

  const form = useForm({
    schema: formSchema,
    mode: 'onBlur',
    values: initialValues,
  });
  return (
    <Form
      form={form}
      onSubmit={async (values) => {
        const { nomination, lastEntryTime, planPrice, noOfPeople, planTitle } = values;
        await mutateAsync({
          planId: planId || '',
          nomination: Number(nomination),
          noOfPeople: noOfPeople ? Number(noOfPeople) : undefined,
          clubId: clubId || '',
          lastEntryTime: lastEntryTime,
          planPrice: Number(planPrice),
          planTitleJp: planTitle || '',
        });
      }}
    >
      <div className="w-full space-y-4.5">
        <div className="grid grid-cols-2 gap-4.5">
          <Input
            label="configurationManagement.plans.page.managePlansModal.planTitleJp"
            {...form.register('planTitle')}
            placeholder={t('configurationManagement.plans.page.placeholder.enterPlanTitle')}
            disabled
            maxLength={50}
          />
          <Input
            label="configurationManagement.plans.page.managePlansModal.planTitleEng"
            {...form.register('planTitleEng')}
            disabled
            placeholder={t('configurationManagement.plans.page.placeholder.enterPlanTitle')}
            maxLength={50}
          />
          <Select
            key={`planTime-${planRequestId || 'new'}`}
            {...form.register('lastEntryTime')}
            placeholder={t('configurationManagement.plans.page.placeholder.selectLastEntryTime')}
            options={timeIntervalOption}
            label="configurationManagement.plans.page.managePlansModal.lastEntryTime"
            disabled={isLoadingPlanData || isError}
          />
          <Input
            label="configurationManagement.plans.page.managePlansModal.planPrice"
            {...form.register('planPrice')}
            placeholder={t('configurationManagement.plans.page.placeholder.enterPlanPrice')}
            type="text"
            inputMode="numeric"
            onlyNumbers
            maxLength={10}
            disabled={isLoadingPlanData || isError}
          />
        </div>
        <div className="grid grid-cols-2 gap-4.5">
          <Select
            key={`planCategory-${planRequestId || 'new'}`}
            {...form.register('nomination')}
            placeholder={t('configurationManagement.plans.page.placeholder.selectNomination')}
            options={numberOfPeopleAndNominationOption}
            label="configurationManagement.plans.page.managePlansModal.nomination"
            disabled={isLoadingPlanData || isError}
          />

          <Select
            key={`planCategory-${planRequestId || 'new'}`}
            {...form.register('noOfPeople')}
            placeholder={t('configurationManagement.plans.page.placeholder.select')}
            options={numberOfPeopleAndNominationOption}
            label="configurationManagement.plans.page.managePlansModal.numOfPeople"
            disabled={isLoadingPlanData || isError}
          />
        </div>
      </div>
    </Form>
  );
};

export default HoslogPlanRequestsForm;
